import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Link, useLocation, useParams} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faSearch,
  faEdit,
  faPlus,
  faTractor,
  faTruck,
  faSeedling,
  faBriefcase,
} from '@fortawesome/pro-regular-svg-icons';
import {reduxToggleSidePanel} from 'appredux/actions';
import {ESidePanelView} from 'constants/index';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  TextField,
  Typography,
  Snackbar,
} from '@material-ui/core';

import {
  apiFarmerLinkedAccounts,
  apiGetAccountStatement,
  apiGetAllFdApplications,
  apiGetFdApplication,
  apiGetDepotOrder,
  apiGetInvoiceSupportingDocuments,
  apiGetUser,
  apiGetUserFdApplications,
} from 'constants/Api';
import {apiGetAccountRestrictions} from 'constants/api/fundingApplications';
import {
  EGPAY_ACCOUNT_TYPE,
  IFdApplicationType,
  ILinkedGpayAccount,
} from 'constants/interfaces';
import {apiGetWalletCashflows} from 'constants/walletCashflowApi';
import type {IWalletCashflow} from 'constants/walletCashflowTypes';
import NavigationService from 'services/NavigationService';
// eslint-disable-next-line max-len
import {ApplicationApprovedCashflowReviewDialog} from 'screens/Dashboard/Applications/ApplicationDetail/Journey/components/ApplicationApprovedCashflowCard';
import WalletCashflowPanel from './WalletCashflowPanel';

import './WalletScreen.scss';

interface IWalletAccount {
  id: string;
  name: string;
  accountNumber: string | null;
  balance: number;
  accountType?: EGPAY_ACCOUNT_TYPE;
  logo?: string;
}

interface IRestriction {
  id: string;
  subCategory?: {
    id: string;
    name: string;
    image?: string;
  } | null;
  categories: {
    id: number;
    name: string;
    image?: string;
  };
  restictionAmount: number;
}

interface IStatementTransaction {
  TransactionDate: string;
  Description?: string;
  Amount: number;
  InvoiceNumber?: string;
  Reference?: string;
}

type TWalletActivityView = 'statement' | 'cashflows' | 'budget';
type TStatementDatePreset =
  | 'today'
  | 'week'
  | 'month'
  | 'quarter'
  | 'sixMonths'
  | 'twelveMonths'
  | 'custom';

const getCategoryIcon = (categoryName: string) => {
  const name = (categoryName || '').toLowerCase();
  if (name.includes('mechanization')) return faTractor;
  if (name.includes('vehicle')) return faTruck;
  if (name.includes('seed')) return faSeedling;
  return faBriefcase;
};

const APPROVED_APPLICATION_STATES = new Set([
  'PENDING_PAYOUT',
  'FINAL_STAGE',
  'CONDITION_PRECEDENT',
  'TERM_SHEET_SIGNATURE',
  'PENDING_SIGNATURES',
]);

function lshDateInputValue(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0',
  )}-${String(date.getDate()).padStart(2, '0')}`;
}

function lshCurrentMonthStart(): string {
  const now = new Date();
  return lshDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1));
}

function lshDateRangeForPreset(preset: TStatementDatePreset): {
  startDate: string;
  endDate: string;
} {
  const today = new Date();
  const start = new Date(today);

  switch (preset) {
    case 'today':
      break;
    case 'week': {
      const day = today.getDay() || 7;
      start.setDate(today.getDate() - day + 1);
      break;
    }
    case 'month':
      start.setDate(1);
      break;
    case 'quarter':
      start.setMonth(Math.floor(today.getMonth() / 3) * 3, 1);
      break;
    case 'sixMonths':
      start.setMonth(today.getMonth() - 6);
      break;
    case 'twelveMonths':
      start.setMonth(today.getMonth() - 12);
      break;
    case 'custom':
      return {
        startDate: lshCurrentMonthStart(),
        endDate: lshDateInputValue(today),
      };
    default:
      break;
  }

  return {
    startDate: lshDateInputValue(start),
    endDate: lshDateInputValue(today),
  };
}

function lshFormatMoney(value: unknown): string {
  return Number(value || 0).toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseInvoiceIdFromReference(refString: string) {
  if (!refString) return null;
  const match = refString.match(/KHULA-(\d+)/);
  return match ? match[1] : null;
}

function lshFundingAskTotal(application?: IFdApplicationType): number {
  return (application?.fundingAsk?.fundingRequestCosts || []).reduce(
    (total, item) => total + Number(item.cost || 0),
    0,
  );
}

function lshApplicationLabel(application: IFdApplicationType): string {
  const name =
    application.company?.name ||
    application.farm?.name ||
    [application.applyingUser?.name, application.applyingUser?.surname]
      .filter(Boolean)
      .join(' ');

  return `${name || 'Application'} #${application.id}`;
}

function lshApplicationMatchesCustomer(
  application: IFdApplicationType,
  customerUserId: string,
): boolean {
  const applyingUser = application.applyingUser;
  return (
    String(applyingUser?.externalId || '') === String(customerUserId) ||
    String(applyingUser?.id || '') === String(customerUserId)
  );
}

function lshFallbackApplicationsForCustomer(customerUserId: string) {
  return apiGetAllFdApplications().then((applications) =>
    (applications || []).filter((application) =>
      lshApplicationMatchesCustomer(application, customerUserId),
    ),
  );
}

function lshCashflowApplicationLabel(cashflow?: IWalletCashflow): string {
  const applicationId =
    cashflow?.creditApplicationRef?.applicationId ||
    cashflow?.creditApplicationRefId;

  return applicationId ? `Application #${applicationId}` : 'Application linked';
}

function lshCashflowApplicationId(cashflow?: IWalletCashflow): string {
  return String(
    cashflow?.creditApplicationRef?.applicationId ||
      cashflow?.creditApplicationRefId ||
      '',
  );
}

function lshCashflowPeriodLabel(cashflow?: IWalletCashflow): string {
  if (cashflow?.periodStart && cashflow?.periodEnd) {
    return `${cashflow.periodStart} to ${cashflow.periodEnd}`;
  }

  return 'Period not set';
}

function lshUnwrapLinkedAccounts(response: any): ILinkedGpayAccount[] {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.getFarmerInputsLinkedAccounts)) {
    return response.getFarmerInputsLinkedAccounts;
  }
  if (Array.isArray(response?.data?.getFarmerInputsLinkedAccounts)) {
    return response.data.getFarmerInputsLinkedAccounts;
  }
  return [];
}

function lshBuildWalletAccounts(
  linkedAccounts: ILinkedGpayAccount[],
): IWalletAccount[] {
  return linkedAccounts.flatMap((wallet) => {
    const companyLogo = wallet.company?.image;

    return (wallet.accounts || []).map((account) => ({
      id: String(wallet.id),
      name:
        account.accountName ||
        account.accountNumber ||
        wallet.company?.name ||
        'Wallet',
      accountNumber: account.accountNumber || null,
      balance: Number(account.AvailableBalance || 0),
      accountType: account.accountType,
      logo: companyLogo,
    }));
  });
}

function WalletScreen() {
  const {customerUserId, walletId} = useParams<{
    customerUserId: string;
    walletId?: string;
  }>();
  const location = useLocation<{
    wallets?: IWalletAccount[];
    selectedWalletId?: string;
  }>();

  const routeWallets = location.state?.wallets;
  const selectedWalletId = location.state?.selectedWalletId;
  const stateWallets: IWalletAccount[] = useMemo(
    () =>
      Array.isArray(routeWallets)
        ? (routeWallets as Array<IWalletAccount | null>)
            .filter(
              (wallet): wallet is IWalletAccount =>
                wallet !== null && wallet !== undefined,
            )
            .map((wallet) => ({
              ...wallet,
              id: String(wallet.id),
              balance: Number(wallet.balance || 0),
              accountNumber: wallet.accountNumber || null,
            }))
        : [],
    [routeWallets],
  );

  const [fetchedWallets, setFetchedWallets] = useState<IWalletAccount[]>([]);
  const [loadingWalletList, setLoadingWalletList] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState<IWalletAccount | null>(
    null,
  );
  const [transactions, setTransactions] = useState<IStatementTransaction[]>([]);
  const [loadingStatement, setLoadingStatement] = useState(false);
  const [openingBalance, setOpeningBalance] = useState<number>(0);
  const [closingBalance, setClosingBalance] = useState<number>(0);
  const [datePreset, setDatePreset] = useState<TStatementDatePreset>('month');
  const [startDate, setStartDate] = useState<string>(
    lshDateRangeForPreset('month').startDate,
  );
  const [endDate, setEndDate] = useState<string>(
    lshDateRangeForPreset('month').endDate,
  );
  const [searchInvoice, setSearchInvoice] = useState<string>('');
  const [activeView, setActiveView] =
    useState<TWalletActivityView>('statement');

  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [loadingInvoiceData, setLoadingInvoiceData] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState<any>(null);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [invoiceDocs, setInvoiceDocs] = useState<any[]>([]);
  const [cashflowRefreshToken, setCashflowRefreshToken] = useState(0);
  const [walletCashflows, setWalletCashflows] = useState<IWalletCashflow[]>([]);
  const [loadingWalletCashflows, setLoadingWalletCashflows] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [linkableApplications, setLinkableApplications] = useState<
    IFdApplicationType[]
  >([]);
  const [selectedApplicationId, setSelectedApplicationId] = useState('');
  const [reviewApplication, setReviewApplication] =
    useState<IFdApplicationType>();
  const [loadingReviewApplication, setLoadingReviewApplication] =
    useState(false);
  const [linkApplicationError, setLinkApplicationError] = useState('');
  const safeWallets = fetchedWallets.length ? fetchedWallets : stateWallets;
  const selectedWalletIdFromRoute = selectedWalletId || walletId;

  const [restrictions, setRestrictions] = useState<IRestriction[]>([]);
  const [loadingRestrictions, setLoadingRestrictions] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [mainTab, setMainTab] = useState<'cashflows' | 'restrictions'>(
    'cashflows',
  );

  useEffect(() => {
    if (!customerUserId) return;

    let isMounted = true;
    setLoadingWalletList(true);

    apiGetUser(customerUserId)
      .then((fetchedUser) => {
        const farmerId = fetchedUser?.farmer?.id;
        if (!farmerId) {
          throw new Error('No farmerId found for this user');
        }
        return apiFarmerLinkedAccounts(String(farmerId));
      })
      .then((linkedAccounts) => {
        if (!isMounted) return;
        setFetchedWallets(
          lshBuildWalletAccounts(lshUnwrapLinkedAccounts(linkedAccounts)),
        );
      })
      .catch((err) => {
        console.error('Error fetching wallets:', err);
        if (isMounted) setFetchedWallets([]);
      })
      .finally(() => {
        if (isMounted) setLoadingWalletList(false);
      });

    return () => {
      isMounted = false;
    };
  }, [customerUserId]);

  useEffect(() => {
    if (!safeWallets.length) {
      if (selectedWallet) setSelectedWallet(null);
      return;
    }

    const preSelected =
      safeWallets.find(
        (wallet) => wallet.id === String(selectedWalletIdFromRoute),
      ) || safeWallets[0];

    if (!selectedWallet || selectedWallet.id !== preSelected.id) {
      setSelectedWallet(preSelected);
    }
  }, [safeWallets, selectedWallet, selectedWalletIdFromRoute]);

  useEffect(() => {
    if (!selectedWallet) return;

    setLoadingStatement(true);
    apiGetAccountStatement(
      selectedWallet.id,
      (selectedWallet.accountType ?? EGPAY_ACCOUNT_TYPE.TPFM) as any,
      startDate,
      endDate,
    )
      .then((result) => {
        const statementArray = result.Statement || [];
        setTransactions(statementArray);

        const totalTxs = statementArray.reduce(
          (sum, tx) => sum + (tx.Amount || 0),
          0,
        );
        setOpeningBalance((result.AvailableBalance ?? 0) - totalTxs);
        setClosingBalance(result.AvailableBalance ?? 0);
      })
      .catch((err) => {
        console.error('Error fetching statement:', err);
        setTransactions([]);
        setOpeningBalance(0);
        setClosingBalance(0);
      })
      .finally(() => setLoadingStatement(false));

    // Fetch restrictions
    fetchRestrictions();
  }, [selectedWallet, startDate, endDate]);

  const fetchRestrictions = useCallback(() => {
    if (!selectedWallet) return;
    setLoadingRestrictions(true);
    apiGetAccountRestrictions(selectedWallet.id)
      .then((data) => {
        setRestrictions(
          (Array.isArray(data) ? data : []).filter((item) => item && item.id),
        );
      })
      .catch((err) => {
        console.error('Error fetching restrictions:', err);
        setRestrictions([]);
      })
      .finally(() => setLoadingRestrictions(false));
  }, [selectedWallet]);

  useEffect(() => {
    if (!selectedWallet) {
      setWalletCashflows([]);
      return;
    }

    setLoadingWalletCashflows(true);
    apiGetWalletCashflows(String(selectedWallet.id))
      .then((cashflows) => setWalletCashflows(cashflows || []))
      .catch((err) => {
        console.error('Error fetching wallet cashflows:', err);
        setWalletCashflows([]);
      })
      .finally(() => setLoadingWalletCashflows(false));
  }, [cashflowRefreshToken, selectedWallet]);

  const displayedTransactions = useMemo(() => {
    if (!searchInvoice) return transactions;
    const search = searchInvoice.toLowerCase();
    return transactions.filter(
      (tx) =>
        tx.InvoiceNumber?.toLowerCase().includes(search) ||
        tx.Reference?.toLowerCase().includes(search),
    );
  }, [searchInvoice, transactions]);

  const primaryWalletCashflow = useMemo(() => {
    return [...walletCashflows].sort((left, right) => {
      const leftTime = Date.parse(left.linkedAt || '') || 0;
      const rightTime = Date.parse(right.linkedAt || '') || 0;
      return rightTime - leftTime;
    })[0];
  }, [walletCashflows]);

  const handleWalletClick = (wallet: IWalletAccount) => {
    setSelectedWallet(wallet);
  };

  const handleAddRestrictionClick = () => {
    if (!selectedWallet) return;
    reduxToggleSidePanel(
      true,
      {
        panelType: ESidePanelView.ADD_WALLET_RESTRICTION,
        data: {
          walletId: selectedWallet.id,
          walletBalance: selectedWallet.balance,
          existingRestrictions: restrictions,
          onRestrictionAdded: () => {
            setToastMessage('Restriction added successfully!');
            fetchRestrictions();
          },
        },
      },
      false,
    );
  };

  const handleEditRestrictionClick = (item: IRestriction) => {
    if (!selectedWallet) return;
    reduxToggleSidePanel(
      true,
      {
        panelType: ESidePanelView.EDIT_WALLET_RESTRICTION,
        data: {
          walletId: selectedWallet.id,
          walletBalance: selectedWallet.balance,
          existingRestrictions: restrictions,
          restriction: {
            category: String(item.subCategory?.id || item.categories?.id || ''),
            maxSpend: item.restictionAmount || 0,
          },
          onRestrictionAdded: () => {
            setToastMessage('Restriction updated successfully!');
            fetchRestrictions();
          },
        },
      },
      false,
    );
  };

  const handleDatePresetChange = (nextPreset: TStatementDatePreset) => {
    setDatePreset(nextPreset);
    if (nextPreset === 'custom') return;

    const range = lshDateRangeForPreset(nextPreset);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  };

  const activityTitle =
    activeView === 'statement'
      ? 'Transaction History'
      : activeView === 'cashflows'
      ? 'Approved cashflows'
      : 'Spent vs approved';

  const handleTransactionClick = (tx: IStatementTransaction) => {
    const invoicePart = parseInvoiceIdFromReference(tx.Reference ?? '');
    if (!invoicePart) {
      alert(`No valid KHULA-### pattern found in Reference: ${tx.Reference}`);
      return;
    }

    const invoiceId = parseInt(invoicePart, 10);
    if (isNaN(invoiceId)) {
      alert(`Parsed invoice ID is not numeric: ${invoicePart}`);
      return;
    }

    setShowInvoiceModal(true);
    setLoadingInvoiceData(true);
    setInvoiceDetails(null);
    setLoadingDocs(true);
    setInvoiceDocs([]);

    apiGetDepotOrder(invoiceId)
      .then((invoiceData) => {
        setInvoiceDetails(invoiceData);
      })
      .catch((err) => {
        console.error('Error fetching invoice data:', err);
        alert('Error fetching invoice data. See console for details.');
      })
      .finally(() => setLoadingInvoiceData(false));

    apiGetInvoiceSupportingDocuments(invoiceId)
      .then((docs) => {
        setInvoiceDocs(docs);
      })
      .catch((err) => {
        console.error('Error fetching invoice docs:', err);
      })
      .finally(() => setLoadingDocs(false));
  };

  const handleViewPdf = (pdfUrl: string) => {
    if (!pdfUrl) return;
    window.open(pdfUrl, '_blank', 'noopener,noreferrer');
  };

  const loadLinkableApplications = () => {
    setLoadingApplications(true);
    setLinkApplicationError('');
    setSelectedApplicationId('');

    apiGetUserFdApplications(customerUserId)
      .then((applications) => {
        if (applications?.length) return applications;
        return lshFallbackApplicationsForCustomer(customerUserId);
      })
      .catch((err) => {
        console.error('User applications lookup failed, using fallback', err);
        return lshFallbackApplicationsForCustomer(customerUserId);
      })
      .then((applications) => {
        const approvedApplications = (applications || [])
          .filter((application) =>
            APPROVED_APPLICATION_STATES.has(
              String(application.applicationState),
            ),
          )
          .filter(
            (application) =>
              !walletCashflows.some(
                (cashflow) =>
                  cashflow.creditApplicationRef?.applicationId ===
                    String(application.id) ||
                  cashflow.creditApplicationRefId === String(application.id),
              ),
          );
        setLinkableApplications(approvedApplications);
      })
      .catch((err) => {
        console.error('Error loading approved applications', err);
        setLinkableApplications([]);
        setLinkApplicationError(
          err?.message || 'Failed to load approved applications.',
        );
      })
      .finally(() => setLoadingApplications(false));
  };

  const handleOpenLinkApplication = () => {
    setLinkDialogOpen(true);
    loadLinkableApplications();
  };

  const handleReviewSelectedApplication = () => {
    if (!selectedApplicationId) return;

    setLoadingReviewApplication(true);
    setLinkApplicationError('');

    apiGetFdApplication(selectedApplicationId)
      .then((application) => {
        if (!application) throw new Error('Application not found.');
        setReviewApplication(application);
        setLinkDialogOpen(false);
      })
      .catch((err) => {
        console.error('Error loading selected application', err);
        setLinkApplicationError(
          err?.message || 'Failed to load selected application.',
        );
      })
      .finally(() => setLoadingReviewApplication(false));
  };

  return (
    <div className="wallet-screen-container">
      <div className="breadcrumb-row">
        <Link to="/customers">Farmers</Link> /{' '}
        <Link to={`/customers/dashboard/${customerUserId}`}>Farmer</Link> /{' '}
        <span>Wallets</span>
      </div>

      <div className="top-row">
        <div className="wallets-list-card">
          <h3>Wallets</h3>
          <div className="wallets-list">
            {loadingWalletList && !safeWallets.length ? (
              <div className="wallets-empty">Loading wallets...</div>
            ) : safeWallets.length === 0 ? (
              <div className="wallets-empty">
                No wallets found for this customer.
              </div>
            ) : (
              safeWallets.map((wallet) => (
                <button
                  type="button"
                  key={wallet.id}
                  className={`wallet-list-item ${
                    selectedWallet?.id === wallet.id ? 'selected' : ''
                  }`}
                  onClick={() => handleWalletClick(wallet)}
                >
                  <span className="wallet-title">
                    {wallet.name || 'Unnamed wallet'}
                  </span>
                  <span className="wallet-subtitle">
                    Balance: R{lshFormatMoney(wallet.balance)}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="wallet-main-panel">
          <div className="wallet-main-panel__header">
            <div>
              <h3>{selectedWallet?.name || 'Wallet'}</h3>
              <p>
                Approved cashflows are now the source for wallet spend limits.
              </p>
            </div>
            <div className="account-tag">
              {selectedWallet?.accountType || 'TPFM'} account
            </div>
          </div>

          <div
            className="wallet-main-tabs"
            style={{
              display: 'flex',
              gap: 16,
              borderBottom: '1px solid #e0e0e0',
              marginBottom: 24,
            }}
          >
            <button
              type="button"
              style={{
                background: 'none',
                border: 'none',
                borderBottom:
                  mainTab === 'cashflows'
                    ? '2px solid #004d40'
                    : '2px solid transparent',
                padding: '8px 16px',
                cursor: 'pointer',
                fontWeight: mainTab === 'cashflows' ? 600 : 400,
                color: mainTab === 'cashflows' ? '#004d40' : '#666',
                outline: 'none',
              }}
              onClick={() => setMainTab('cashflows')}
            >
              Cashflows
            </button>
            <button
              type="button"
              style={{
                background: 'none',
                border: 'none',
                borderBottom:
                  mainTab === 'restrictions'
                    ? '2px solid #004d40'
                    : '2px solid transparent',
                padding: '8px 16px',
                cursor: 'pointer',
                fontWeight: mainTab === 'restrictions' ? 600 : 400,
                color: mainTab === 'restrictions' ? '#004d40' : '#666',
                outline: 'none',
              }}
              onClick={() => {
                setMainTab('restrictions');
                setActiveView('statement');
              }}
            >
              Wallet Restrictions
            </button>
          </div>

          {mainTab === 'cashflows' ? (
            <>
              <div className="wallet-main-panel__summary">
                <div>
                  <span>Available balance</span>
                  <strong>R{lshFormatMoney(selectedWallet?.balance)}</strong>
                </div>
                <div>
                  <span>Account number</span>
                  <strong>{selectedWallet?.accountNumber || '-'}</strong>
                </div>
              </div>

              <div
                className={`wallet-main-panel__application ${
                  primaryWalletCashflow ? 'linked' : 'empty'
                }`}
              >
                <div>
                  <span>Approved application</span>
                  <strong>
                    {loadingWalletCashflows
                      ? 'Checking linked application...'
                      : primaryWalletCashflow
                      ? lshCashflowApplicationLabel(primaryWalletCashflow)
                      : 'No approved application linked'}
                  </strong>
                  <p>
                    {loadingWalletCashflows
                      ? 'Loading approved cashflow status.'
                      : primaryWalletCashflow
                      ? `${walletCashflows.length} approved cashflow${
                          walletCashflows.length === 1 ? '' : 's'
                        } linked · R${lshFormatMoney(
                          primaryWalletCashflow.totalAllocatedAmount,
                        )} · ${lshCashflowPeriodLabel(primaryWalletCashflow)}`
                      : 'Link an approved application so this wallet can use cashflow spend limits.'}
                  </p>
                </div>
                {selectedWallet &&
                primaryWalletCashflow &&
                lshCashflowApplicationId(primaryWalletCashflow) ? (
                  <Link
                    className="wallet-main-panel__application-action secondary"
                    to={`/applications/${lshCashflowApplicationId(
                      primaryWalletCashflow,
                    )}`}
                  >
                    View application
                  </Link>
                ) : selectedWallet ? (
                  <button
                    className="wallet-main-panel__application-action primary"
                    type="button"
                    onClick={handleOpenLinkApplication}
                  >
                    Link approved application
                  </button>
                ) : null}
              </div>
            </>
          ) : (
            <div className="wallet-restrictions-tab-content">
              <Typography variant="h6" style={{marginBottom: 16}}>
                Manage Restrictions
              </Typography>
              {!loadingRestrictions && restrictions.length === 0 && (
                <p>No restrictions found for this wallet.</p>
              )}
              <div className="category-list">
                {restrictions.map((item) => {
                  const categoryName =
                    item.subCategory?.name ||
                    item?.categories?.name ||
                    'Unknown category';
                  return (
                    <div className="category-block" key={item.id}>
                      <div className="category-icon-wrapper">
                        <FontAwesomeIcon icon={getCategoryIcon(categoryName)} />
                      </div>
                      <div className="category-details">
                        <div className="category-name">{categoryName}</div>
                        <div className="category-amount">
                          R{(item.restictionAmount ?? 0).toLocaleString()} left
                        </div>
                      </div>
                      <button
                        className="edit-button"
                        onClick={() => handleEditRestrictionClick(item)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                    </div>
                  );
                })}

                <div
                  className="category-block add-new"
                  onClick={handleAddRestrictionClick}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faPlus}
                      style={{fontSize: 24, color: '#666'}}
                    />
                    <div className="category-name">Add New</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="transaction-history-card">
        <div className="transaction-history-header">
          <div className="wallet-activity-header-row">
            <h3>{activityTitle}</h3>
          </div>

          <div className="activity-controls-row">
            {activeView === 'statement' ? (
              <div className="search-paper">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by invoice # or reference"
                  value={searchInvoice}
                  disabled={mainTab === 'restrictions'}
                  onChange={(event) => setSearchInvoice(event.target.value)}
                />
              </div>
            ) : null}
            <div className="actions-right">
              <div className="wallet-activity-tabs">
                {[
                  {value: 'statement', label: 'Statement'},
                  {value: 'cashflows', label: 'Cashflow view'},
                  {value: 'budget', label: 'Spent vs approved'},
                ].map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    className={activeView === tab.value ? 'active' : ''}
                    disabled={
                      mainTab === 'restrictions' && tab.value !== 'statement'
                    }
                    onClick={() =>
                      setActiveView(tab.value as TWalletActivityView)
                    }
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              {activeView === 'statement' ? (
                <div className="statement-range-controls">
                  <select
                    className="statement-range-select"
                    value={datePreset}
                    disabled={mainTab === 'restrictions'}
                    onChange={(event) =>
                      handleDatePresetChange(
                        event.target.value as TStatementDatePreset,
                      )
                    }
                  >
                    <option value="today">Today</option>
                    <option value="week">This week</option>
                    <option value="month">This month</option>
                    <option value="quarter">This quarter</option>
                    <option value="sixMonths">6 months</option>
                    <option value="twelveMonths">12 months</option>
                    <option value="custom">Custom</option>
                  </select>
                  {datePreset === 'custom' ? (
                    <>
                      <input
                        type="date"
                        className="date-filter"
                        value={startDate}
                        disabled={mainTab === 'restrictions'}
                        onChange={(event) => setStartDate(event.target.value)}
                      />
                      <input
                        type="date"
                        className="date-filter"
                        value={endDate}
                        disabled={mainTab === 'restrictions'}
                        onChange={(event) => setEndDate(event.target.value)}
                      />
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {activeView === 'statement' ? (
          loadingStatement ? (
            <p>Loading statement...</p>
          ) : (
            <>
              <div className="transaction-balance-row">
                <p>
                  <strong>Opening Balance:</strong> R
                  {lshFormatMoney(openingBalance)}
                </p>
                <p>
                  <strong>Closing Balance:</strong> R
                  {lshFormatMoney(closingBalance)}
                </p>
              </div>
              <table className="transactions-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Reference</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {displayedTransactions.length === 0 ? (
                    <tr>
                      <td colSpan={5}>No transactions found.</td>
                    </tr>
                  ) : (
                    displayedTransactions.map((tx, idx) => (
                      <tr key={`${tx.Reference || tx.TransactionDate}-${idx}`}>
                        <td>{tx.TransactionDate}</td>
                        <td>{tx.Reference || '-'}</td>
                        <td>{tx.Description || '-'}</td>
                        <td>R{lshFormatMoney(tx.Amount)}</td>
                        <td>
                          <FontAwesomeIcon
                            icon={faArrowRight}
                            className="action-icon"
                            onClick={() => handleTransactionClick(tx)}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </>
          )
        ) : loadingWalletCashflows ? (
          <div className="wallet-cashflow-panel loading">
            <CircularProgress size={28} />
            <span>Loading wallet cashflows...</span>
          </div>
        ) : selectedWallet ? (
          <WalletCashflowPanel
            walletId={String(selectedWallet.id)}
            refreshToken={cashflowRefreshToken}
            activeTab={activeView === 'budget' ? 'budget' : 'cashflows'}
            hideTabs
            onTabChange={(tab) => setActiveView(tab)}
            onLinkApplication={handleOpenLinkApplication}
            onViewApplication={(applicationId) =>
              NavigationService.navigate(`/applications/${applicationId}`)
            }
          />
        ) : null}
      </div>

      {mainTab === 'cashflows' && (
        <Dialog
          open={linkDialogOpen}
          onClose={() => !loadingReviewApplication && setLinkDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Link approved application</DialogTitle>
          <DialogContent dividers>
            {loadingApplications ? (
              <div className="wallet-link-dialog__loading">
                <CircularProgress size={20} />
                <Typography variant="body2" color="textSecondary">
                  Loading approved applications...
                </Typography>
              </div>
            ) : linkableApplications.length ? (
              <TextField
                select
                label="Approved application"
                variant="outlined"
                size="small"
                fullWidth
                value={selectedApplicationId}
                onChange={(event) =>
                  setSelectedApplicationId(event.target.value)
                }
                helperText="Choose the application cashflow to publish to this wallet."
              >
                <MenuItem value="">Select application</MenuItem>
                {linkableApplications.map((application) => (
                  <MenuItem key={application.id} value={application.id}>
                    {lshApplicationLabel(application)} - R
                    {lshFormatMoney(lshFundingAskTotal(application))}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No approved applications are available for this farmer.
              </Typography>
            )}

            {linkApplicationError ? (
              <Typography
                className="wallet-link-dialog__error"
                variant="body2"
                color="error"
              >
                {linkApplicationError}
              </Typography>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              disabled={loadingReviewApplication}
              onClick={() => setLinkDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              disabled={!selectedApplicationId || loadingReviewApplication}
              onClick={handleReviewSelectedApplication}
            >
              {loadingReviewApplication ? 'Loading...' : 'Review cashflow'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {mainTab === 'cashflows' && reviewApplication && selectedWallet ? (
        <ApplicationApprovedCashflowReviewDialog
          application={reviewApplication}
          open={Boolean(reviewApplication)}
          defaultWalletId={String(selectedWallet.id)}
          defaultWalletOption={{
            id: String(selectedWallet.id),
            name:
              selectedWallet.name ||
              selectedWallet.accountNumber ||
              'Selected wallet',
            accountName: selectedWallet.name,
            accountNumber: selectedWallet.accountNumber || undefined,
            availableBalance: selectedWallet.balance,
          }}
          lockWalletSelection
          confirmLabel="Link application"
          onClose={() => setReviewApplication(undefined)}
          onPublished={() => {
            setReviewApplication(undefined);
            setCashflowRefreshToken((current) => current + 1);
            setActiveView('budget');
          }}
        />
      ) : null}

      <Dialog
        open={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        aria-labelledby="invoice-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="invoice-dialog-title" style={{fontWeight: 600}}>
          Invoice Details
        </DialogTitle>

        <DialogContent>
          {loadingInvoiceData ? (
            <div style={{textAlign: 'center', padding: '20px'}}>
              <CircularProgress />
            </div>
          ) : invoiceDetails ? (
            <>
              <div className="invoice-details-container">
                <Paper elevation={0} className="invoice-section">
                  <Typography variant="subtitle1" className="section-title">
                    Order Summary
                  </Typography>
                  <div className="section-row">
                    <div className="label">Invoice #:</div>
                    <div className="value">{invoiceDetails.invoiceNumber}</div>
                  </div>
                  <div className="section-row">
                    <div className="label">Date:</div>
                    <div className="value">
                      {new Date(
                        parseInt(invoiceDetails.insertedDate, 10),
                      ).toLocaleString()}
                    </div>
                  </div>
                  <div className="section-row">
                    <div className="label">Total:</div>
                    <div className="value">
                      R{lshFormatMoney(invoiceDetails.total)}
                    </div>
                  </div>
                  <div className="section-row">
                    <div className="label">Logistics Cost:</div>
                    <div className="value">
                      R{lshFormatMoney(invoiceDetails.logisticsCost)}
                    </div>
                  </div>
                </Paper>

                <Divider style={{margin: '16px 0'}} />

                <Paper elevation={0} className="invoice-section">
                  <Typography variant="subtitle1" className="section-title">
                    Product(s)
                  </Typography>
                  {invoiceDetails.orders && invoiceDetails.orders.length > 0 ? (
                    invoiceDetails.orders.map((order: any, idx: number) => (
                      <div key={idx} className="product-block">
                        <div className="product-name">
                          {order.product?.name || 'Unnamed product'}
                        </div>
                        <div className="product-subtitle">
                          Quantity: {order.quantity} x R{order.price}
                        </div>
                        <div className="product-subtitle">
                          Ingredient:{' '}
                          {order.product?.activeIngredient || 'Unknown'}
                        </div>
                      </div>
                    ))
                  ) : (
                    <Typography variant="body2">
                      No products found on this invoice.
                    </Typography>
                  )}
                </Paper>

                <Divider style={{margin: '16px 0'}} />

                <Paper elevation={0} className="invoice-section">
                  <Typography variant="subtitle1" className="section-title">
                    Farmer
                  </Typography>
                  <div className="section-row">
                    <div className="label">Name:</div>
                    <div className="value">
                      {invoiceDetails.farmer?.name}{' '}
                      {invoiceDetails.farmer?.surname}
                    </div>
                  </div>
                  <div className="section-row">
                    <div className="label">Email:</div>
                    <div className="value">{invoiceDetails.farmer?.email}</div>
                  </div>
                  <div className="section-row">
                    <div className="label">Phone:</div>
                    <div className="value">{invoiceDetails.farmer?.phone}</div>
                  </div>
                  <div className="section-row">
                    <div className="label">Farm Size:</div>
                    <div className="value">
                      {invoiceDetails.farmer?.farm?.sizeHectares} ha
                    </div>
                  </div>
                </Paper>

                <Divider style={{margin: '16px 0'}} />

                <Paper elevation={0} className="invoice-section">
                  <Typography variant="subtitle1" className="section-title">
                    Status
                  </Typography>
                  <div className="section-row">
                    <div className="label">Current:</div>
                    <div className="value">
                      {invoiceDetails.currOrderStatus?.description ?? 'N/A'}
                    </div>
                  </div>
                </Paper>
              </div>

              <Divider style={{margin: '16px 0'}} />

              <Paper elevation={0} className="invoice-section">
                <Typography variant="subtitle1" className="section-title">
                  Supporting Documents
                </Typography>

                {loadingDocs ? (
                  <div style={{margin: '8px 0'}}>
                    <CircularProgress size={20} />
                    <span style={{marginLeft: 8}}>Loading documents...</span>
                  </div>
                ) : null}

                {!loadingDocs && invoiceDocs.length === 0 ? (
                  <Typography variant="body2">
                    No supporting documents found.
                  </Typography>
                ) : null}
                {!loadingDocs && invoiceDocs.length > 0 ? (
                  <>
                    {invoiceDocs.map((doc) => (
                      <div key={doc.id} className="document-item">
                        <div className="doc-title">{doc.title}</div>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleViewPdf(doc.url)}
                          style={{marginLeft: 'auto'}}
                        >
                          View
                        </Button>
                      </div>
                    ))}
                  </>
                ) : null}
              </Paper>
            </>
          ) : (
            <Typography variant="body2">No invoice data loaded.</Typography>
          )}
        </DialogContent>

        <DialogActions>
          {invoiceDetails?.documentUrl ? (
            <Button
              onClick={() => handleViewPdf(invoiceDetails.documentUrl)}
              color="primary"
              variant="outlined"
            >
              View PDF Invoice
            </Button>
          ) : null}
          <Button
            onClick={() => setShowInvoiceModal(false)}
            color="primary"
            variant="contained"
            style={{fontWeight: 600}}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toastMessage}
        onClose={() => setToastMessage('')}
        autoHideDuration={3000}
        message={toastMessage}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      />
    </div>
  );
}

export default WalletScreen;
